import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Styles } from '@t2c/shared-fe';
import React, { useContext, useEffect, useState } from 'react';
import { useGetAllAiMediaData } from '../../../t2c-fe/services';
import { NextImage } from '@sdf/base';
import { useBrollPage } from '../../../t2c-fe/hooks/broll/useBrollPage';
import { BrollClipsSection } from './BrollClipsSection';
import { UploadTypes } from '../../../t2c-fe/@types';
import { CreditDialog } from '../create-video/CreditDialog';
import { BrollClipProccessingModal } from './BrollClipProccessingModal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FetchBrollCaptionParams } from '../../../t2c-fe/services/broll-caption/fetchBrollCaptionData';
import {
  NumGenerationsEnum,
  PAGE_DATA_LIMIT,
  AiVideoModelEnum,
  EXTENDED_ASPECT_RATIO_OPTIONS,
} from '../../../t2c-fe/utils';
import { useRouter } from 'next/router';
import { getAiMediaById } from '../../../t2c-fe/services/ai-generate/getAiMediaById';
import { AspectRatios } from '@t2c/ai-video-editor';
import { useDialog } from 'apps/t2c-fe/hooks/useDialog';
import { BrowseBRollModal } from '../edit-video/modals/BrowseBRoll/BrowseBRollModal';
import { UploadImage } from './UploadImage';
import { UploadVideo } from './UploadVideo';
import { SelectAvatarModal } from '../talking-face/SelectAvatar';
import LoadingButton from '@mui/lab/LoadingButton';
import { CreditContext } from 'apps/t2c-fe/context/CreditProvider';
import { RenderGateModal } from '../common/RenderGateModal';
import ImageEditor from '../common/ImageEditor';

export const PremiumBadge = () => {
  return (
    <Box
      component="span"
      sx={{
        fontSize: '10px',
        fontWeight: 800,
        textTransform: 'uppercase',
        background: 'linear-gradient(45deg, #c084fc, #db2777)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        border: '1px solid rgba(192, 132, 252, 0.3)',
        padding: '2px 6px',
        borderRadius: '4px',
        marginLeft: '8px',
      }}
    >
      Premium
    </Box>
  );
};

export const BrollPage = () => {
  const [params, setParams] = useState<FetchBrollCaptionParams>({
    sortOrder: 'desc',
    limit: PAGE_DATA_LIMIT,
    sortBy: 'updatedAt',
  });

  const { query } = useRouter();

  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage } = useGetAllAiMediaData({
    type: UploadTypes.Video,
    ...params,
  });
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { modelDetails, userPlanData, agentPlanData } = useContext(CreditContext);
  const { push } = useRouter();

  const {
    uploadURL,
    isImage,
    setIsImage,
    maxCharLimit,
    setMaxCharLimit,
    supportedFormats,
    clipGenerationData,
    setClipGenerationData,
    handleDataChange,
    handleAudioToggle,
    handleEnhancePromptToggle,
    handleClipGenerate,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    isCheckingEligibility,
    requiredCreditsData,
    handleConfirmGeneration,
    clipGenerationLoading,
    isProccessingModalOpen,
    closeProccessingModal,
    newGenerationData,
    setUploadURL,
    setUploadTailURL,
    handleUpgrade,
    abortController,
    abortTailController,
    handleMediaSelect,
    uploadTailURL,
    setIsSelectedTail,
    handleUpdateStartImageId,
    handleUpdateEndImageId,
    selectedCharacterImage,
    setSelectedCharacterImage,
    handleCharacterSelect,
    isSelectCharacterDialog,
    openSelectCharacterDialog,
    closeSelectCharacterDialog,
    handleBodyControlToggle,
    handlePromptEnhance,
    renderGateModalMode,
    openRenderGateModal,
    setRenderGateModalMode,
    setOpenRenderGateModal,
    isEnhancing,
    preValidator,
    isTailuploading,
    setIsTailUploading,
    isUploading,
    setIsUploading,
    applyModelDefaults,
    showEditor,
    setShowEditor,
    imageFile,
    setImageFile,
    errorMsg,
    setErrorMsg,
    handleMenuClick,
  } = useBrollPage({ refetchBrollClips: refetch });

  const {
    isDialogOpen: isOpenBrowseBRollModal,
    openDialog: openBrowseBRollModal,
    closeDialog: CloseBrowseBRollModal,
  } = useDialog();

  const handleClickStartImage = () => {
    setIsImage(true);
    setIsSelectedTail(false);
    openBrowseBRollModal();
  };
  const handleClickVideo = () => {
    setIsImage(false);
    setIsSelectedTail(false);
    openBrowseBRollModal();
  };
  const handleClickEndImage = () => {
    setIsImage(true);
    setIsSelectedTail(true);
    openBrowseBRollModal();
  };

  const handleClearInput = () => {
    if (clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO) {
      handleUpdateStartImageId('', '');
    } else {
      handleDataChange('prompt', '');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const editId = Array.isArray(query.edit) ? query.edit[0] : query.edit;
        const editData = await getAiMediaById({ id: editId });
        handleDataChange('prompt', editData?.[0]?.prompt);
      } catch (error) {
        console.error('Error fetching AI media:', error);
      }
    };

    if (query?.edit) {
      fetchData();
    }
  }, [query?.edit]);

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.creationForm,
          borderWidth: isExtraSmall ? 'none' : '3px',
          padding: isExtraSmall ? 0 : '32px',
        }}
      >
        <Typography fontSize={24} fontWeight={500} sx={styles.text}>
          AI Video
        </Typography>

        {clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO ? (
          <UploadVideo
            uploadURL={uploadURL}
            abortController={abortController}
            openBrowseBRollModal={handleClickVideo}
            handleUpdateImage={handleUpdateStartImageId}
            uploading={isUploading}
            setUploading={setIsUploading}
            limit={modelDetails[clipGenerationData.model].maxImageSize}
            isFullPage
          />
        ) : (
          <>
            {!isExtraSmall && (
              <Box sx={styles.subHeadingBox}>
                <Box>
                  <NextImage src={'/assets/icons/broll.svg'} alt="" width={39} height={24} />
                </Box>
                <Typography sx={styles.text}>
                  Generate stunning AI video clips by simply describing the scene. Perfect for
                  enhancing your videos with captivating visuals.
                </Typography>
              </Box>
            )}

            <Typography sx={styles.text}>
              What type of AI video clip would you like to create?
            </Typography>

            <Box sx={{ position: 'relative' }}>
              <TextField
                multiline
                fullWidth
                placeholder={
                  clipGenerationData.model == AiVideoModelEnum.KLING_V3_MOTION_CONTROL
                    ? 'E.g., Smooth cinematic motion, slow dolly forward, stable camera, subtle parallax, realistic depth of field, no jitter or warping. (Optional)'
                    : 'E.g., Vibrant cityscape with lit skyscrapers, busy streets, and glowing neon signs.'
                }
                rows={isExtraSmall ? 4 : 8}
                value={clipGenerationData.prompt}
                onChange={(e) => handleDataChange('prompt', e.target.value.slice(0, maxCharLimit))}
                sx={styles.textField}
                inputProps={{ maxLength: maxCharLimit }}
              />
              <Tooltip title="Enhance prompt with AI" placement="top">
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 4,
                    zIndex: 10,
                  }}
                  disabled={!clipGenerationData.prompt.trim() || isEnhancing}
                  onClick={handlePromptEnhance}
                >
                  {isEnhancing ? (
                    <CircularProgress size={20} />
                  ) : (
                    <NextImage
                      src={'/assets/icons/create-video_default.svg'}
                      alt=""
                      width={24}
                      height={24}
                    />
                  )}
                </IconButton>
              </Tooltip>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: (theme) => theme.palette.text.lighterText2, pl: '2px' }}
              >
                {clipGenerationData?.prompt?.length || 0}/{maxCharLimit} characters
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1 }}>
                {modelDetails[clipGenerationData.model].showEnhancePrompt && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      mt: 1,
                    }}
                  >
                    <Typography sx={{ ...styles.label, mt: 0.5 }}>Enhance Prompt</Typography>
                    <Switch
                      checked={clipGenerationData.enhancePrompt}
                      onChange={handleEnhancePromptToggle}
                    />
                  </Box>
                )}
                {modelDetails[clipGenerationData.model].showAudio && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      mt: 1,
                    }}
                  >
                    <Typography sx={{ ...styles.label, mt: 0.5 }}>Generate Audio</Typography>
                    <Switch
                      checked={clipGenerationData.generateAudio}
                      onChange={handleAudioToggle}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}

        {isExtraSmall && (
          <Box sx={styles.btns}>
            <Button
              variant="outlined"
              sx={{
                fontWeight: 500,
                color: (theme) => theme.palette.text.primary,
                backgroundColor: (theme) => theme.palette.background.secondary,
                borderColor: (theme) => theme.palette.background.border,
              }}
              onClick={handleClearInput}
              disabled={isUploading || isTailuploading}
            >
              Clear Input
            </Button>
            <LoadingButton
              variant="contained"
              sx={styles.createBtn}
              endIcon={<NextImage src={'/assets/icons/Ai.svg'} alt="" width={24} height={24} />}
              onClick={handleClipGenerate}
              disabled={isFetchingRequiredCredits || clipGenerationLoading || isCheckingEligibility}
              loading={isUploading || isTailuploading}
            >
              Create
            </LoadingButton>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 4, flexDirection: isSmall ? 'column' : 'row' }}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              gap: 4,
              flexDirection: isExtraSmall ? 'column' : 'row',
            }}
          >
            <Box flex={1} order={isExtraSmall ? 2 : 1}>
              <Typography sx={styles.text}>Enter Name</Typography>

              <TextField
                fullWidth
                placeholder="Name"
                value={clipGenerationData.name}
                onChange={(e) => handleDataChange('name', e.target.value)}
                size="small"
                sx={{
                  ...styles.textField,
                  height: '56px', // Adjust height to match Select
                  '& .MuiInputBase-root': {
                    height: '100%', // Ensure the input height fills the container
                  },
                }}
              />
            </Box>
            <Box flex={1} order={isExtraSmall ? 1 : 2}>
              <Typography sx={styles.label}>Model</Typography>
              <Select
                labelId="model-select-label"
                value={clipGenerationData.model}
                onChange={(e) => applyModelDefaults(e.target.value as AiVideoModelEnum)}
                sx={{
                  width: '100%',
                  backgroundColor: (theme) => theme.palette.background.secondary,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: styles.dropdownProps,
                  },
                }}
                renderValue={(selected) => (
                  <Box>
                    {selected} {modelDetails[selected].isPremium && <PremiumBadge />}
                  </Box>
                )}
              >
                {Object.values(AiVideoModelEnum).map((output) => (
                  <MenuItem key={output} value={output} onClick={() => handleMenuClick(output)}>
                    <Box>
                      <Box>
                        {output} {modelDetails[output].isPremium && <PremiumBadge />}
                      </Box>
                      {output === AiVideoModelEnum.RUNWAY_ACT_TWO && (
                        <NextImage
                          src="/assets/RunwayLogo.svg"
                          alt="Runway"
                          width={100}
                          height={10}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', gap: 4, flexDirection: 'row' }}>
            <Box flex={1}>
              <Typography sx={styles.label}>Generations</Typography>
              <Select
                labelId="numGenerations-select-label"
                value={clipGenerationData.numGenerations}
                onChange={(e) =>
                  handleDataChange('numGenerations', e.target.value as NumGenerationsEnum)
                }
                sx={{
                  width: '100%',
                  backgroundColor: (theme) => theme.palette.background.secondary,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: styles.dropdownProps,
                  },
                }}
              >
                {Object.values(NumGenerationsEnum).map((output) => (
                  <MenuItem key={output} value={output}>
                    {output}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box flex={1}>
              <Typography sx={styles.label}>Duration(in sec)</Typography>
              <Select
                labelId="duration-select-label"
                value={
                  clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL ||
                  clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO
                    ? 'Video Duration'
                    : clipGenerationData.duration.toString()
                }
                onChange={(e) => handleDataChange('duration', e.target.value.toString())}
                sx={{
                  width: '100%',
                  backgroundColor: (theme) => theme.palette.background.secondary,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: styles.dropdownProps,
                  },
                }}
              >
                {clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL ||
                clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO ? (
                  <MenuItem value="Video Duration">Video Duration</MenuItem>
                ) : (
                  modelDetails[clipGenerationData.model].durationPerModel.map((output) => (
                    <MenuItem key={output} value={output.toString()}>
                      {output}
                    </MenuItem>
                  ))
                )}
              </Select>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexDirection: isSmall ? 'column' : 'row' }}>
          <Box sx={{ flex: 1, order: 0, display: 'flex', flexDirection: 'row', gap: 2 }}>
            {modelDetails[clipGenerationData.model]?.showVideo && (
              <Box>
                <Typography sx={styles.text}>Reference Video*</Typography>
                <UploadVideo
                  uploadURL={uploadURL}
                  abortController={abortController}
                  openBrowseBRollModal={handleClickVideo}
                  handleUpdateImage={handleUpdateStartImageId}
                  uploading={isUploading}
                  setUploading={setIsUploading}
                  limit={modelDetails[clipGenerationData.model].maxImageSize}
                  preValidator={preValidator}
                />
              </Box>
            )}
            {modelDetails[clipGenerationData.model].showUpload && (
              <Box>
                <Typography sx={styles.text}>
                  Start Image{' '}
                  {modelDetails[clipGenerationData.model].isStartImageRequired ? '*' : ''}
                </Typography>
                <UploadImage
                  uploadURL={uploadURL}
                  abortController={abortController}
                  openBrowseBRollModal={handleClickStartImage}
                  handleUpdateImage={handleUpdateStartImageId}
                  uploading={isUploading}
                  setUploading={setIsUploading}
                  limit={modelDetails[clipGenerationData.model].maxImageSize}
                  preValidator={preValidator}
                  orientation={clipGenerationData.orientation}
                  resolution={clipGenerationData.resolution}
                  imageFile={imageFile}
                  setImageFile={setImageFile}
                  errorMsg={errorMsg}
                  setErrorMsg={setErrorMsg}
                  setIsImage={setIsImage}
                />
              </Box>
            )}
            {modelDetails[clipGenerationData.model].showTailUpload && (
              <Box>
                <Typography sx={styles.text}>
                  {clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL
                    ? 'Reference Image*'
                    : 'End Image'}
                </Typography>
                <UploadImage
                  uploadURL={uploadTailURL}
                  abortController={abortTailController}
                  openBrowseBRollModal={handleClickEndImage}
                  handleUpdateImage={handleUpdateEndImageId}
                  uploading={isTailuploading}
                  setUploading={setIsTailUploading}
                  limit={modelDetails[clipGenerationData.model].maxImageSize}
                  preValidator={preValidator}
                  orientation={clipGenerationData.orientation}
                  resolution={clipGenerationData.resolution}
                  imageFile={imageFile}
                  setImageFile={setImageFile}
                  errorMsg={errorMsg}
                  setErrorMsg={setErrorMsg}
                  setIsImage={setIsImage}
                />
              </Box>
            )}
            {modelDetails[clipGenerationData.model].showCharacter && (
              <Box>
                {selectedCharacterImage && (
                  <Typography sx={styles.label}>Selected Character</Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {selectedCharacterImage && (
                    <Box sx={styles.uploader}>
                      <NextImage src={selectedCharacterImage} height={100} width={100} alt="" />
                    </Box>
                  )}

                  <Box sx={styles.CharacterSelectBtn} onClick={openSelectCharacterDialog}>
                    <NextImage
                      src={'/assets/icons/Avatars.svg'}
                      alt=""
                      width={49}
                      height={49}
                      style={{ height: 'auto' }}
                    />
                    <Typography fontWeight={500}>
                      {clipGenerationData.uploadTailId
                        ? 'Choose other Character'
                        : 'Choose Character'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {modelDetails[clipGenerationData.model].showBodyControl && (
              <Box>
                <Typography sx={{ ...styles.label, mt: 0.5 }}>Control Body</Typography>
                <Switch
                  checked={clipGenerationData.bodyControl}
                  onChange={handleBodyControlToggle}
                />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              gap: 4,
              flexDirection: { sm: 'row', xs: 'column' },
              order: isSmall ? -1 : 1,
            }}
          >
            {modelDetails[clipGenerationData.model].orientationPerModel.length > 0 && (
              <Box flex={1}>
                <Typography sx={styles.label}>Orientation</Typography>
                <Select
                  labelId="orientation-select-label"
                  value={clipGenerationData.orientation}
                  onChange={(e) => handleDataChange('orientation', e.target.value)}
                  sx={{
                    width: '100%',
                    backgroundColor: (theme) => theme.palette.background.secondary,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: styles.dropdownProps,
                    },
                  }}
                >
                  {EXTENDED_ASPECT_RATIO_OPTIONS.filter((option) =>
                    modelDetails[clipGenerationData.model].orientationPerModel.includes(
                      option.value
                    )
                  ).map(({ id, value, icon, label, name }) => (
                    <MenuItem key={id} value={value} sx={styles.menuItem}>
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Box sx={{ width: 35, display: 'flex', justifyContent: 'center' }}>
                          {icon}
                        </Box>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography variant="body2" sx={styles.menuLabel}>
                            {label} {name}
                          </Typography>
                        </Stack>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
            {modelDetails[clipGenerationData.model].resolutionPerModel.length > 0 && (
              <Box flex={1}>
                <Typography sx={styles.label}>Resolution</Typography>
                <Select
                  labelId="resolution-select-label"
                  value={clipGenerationData.resolution}
                  onChange={(e) => handleDataChange('resolution', e.target.value)}
                  sx={{
                    width: '100%',
                    backgroundColor: (theme) => theme.palette.background.secondary,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: styles.dropdownProps,
                    },
                  }}
                >
                  {modelDetails[clipGenerationData.model].resolutionPerModel.map((output) => (
                    <MenuItem key={output} value={output}>
                      {output}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            {clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL && (
              <>
                <Box flex={1}>
                  {/* Label + info tooltip */}
                  <Stack direction="row" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography sx={styles.label}>Character Orientation</Typography>

                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography fontSize={12}>
                            <b>Image:</b> The character's orientation matches the reference image,
                            while movements and expressions still follow the video motion reference.
                            Camera motion and other elements can be customized via prompts.
                          </Typography>
                          <Typography fontSize={12} mt={1}>
                            <b>Video:</b> The character's orientation, movements, expressions, and
                            camera motion follow the video motion reference. Other details can be
                            adjusted using prompts.
                          </Typography>
                        </Box>
                      }
                      enterTouchDelay={0}
                      leaveTouchDelay={3000}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            borderRadius: 2,
                            maxWidth: 260,
                          },
                        },
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: '#60A5FA',
                          cursor: 'pointer',
                        }}
                      />
                    </Tooltip>
                  </Stack>

                  {/* Select */}
                  <Select
                    value={clipGenerationData.bodyControl ? 'video' : 'image'}
                    onChange={(e) =>
                      handleBodyControlToggle({
                        target: {
                          checked: e.target.value === 'video',
                        },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    sx={{
                      width: '100%',
                      backgroundColor: (theme) => theme.palette.background.secondary,
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: styles.dropdownProps,
                      },
                    }}
                  >
                    <MenuItem value="image" sx={styles.menuItem}>
                      Image
                    </MenuItem>

                    <MenuItem value="video" sx={styles.menuItem}>
                      Video
                    </MenuItem>
                  </Select>
                </Box>

                <Box flex={1}>
                  <Typography sx={{ ...styles.label, mt: 0.5 }}>Keep Audio</Typography>
                  <Switch checked={clipGenerationData.generateAudio} onChange={handleAudioToggle} />
                </Box>
              </>
            )}

            {clipGenerationData.model === AiVideoModelEnum.KLING_V3_PRO && (
              <Box flex={1}>
                <Typography sx={styles.label}>Multi Shot Generation</Typography>
                <Select
                  labelId="multiShot-select-label"
                  value={clipGenerationData.multiShot}
                  onChange={(e) => handleDataChange('multiShot', e.target.value.toString())}
                  sx={{
                    width: '100%',
                    backgroundColor: (theme) => theme.palette.background.secondary,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: styles.dropdownProps,
                    },
                  }}
                >
                  {modelDetails[clipGenerationData.model].mutliShot.map((output) => (
                    <MenuItem key={output} value={output.toString()}>
                      {output}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
          </Box>
        </Box>

        {/* <Box sx={styles.exampleBox}>
          <Typography sx={styles.text}>Example Prompts</Typography>
          <Typography sx={styles.text}>
            1. Vibrant cityscape with lit skyscrapers, busy streets, and glowing neon signs.
            <br />
            2. A close-up of fresh coffee being poured into a cup, with steam rising.
          </Typography>
        </Box> */}

        {!isExtraSmall && (
          <Box sx={styles.btns}>
            <Button
              variant="outlined"
              sx={{
                fontWeight: 500,
                color: (theme) => theme.palette.text.primary,
                backgroundColor: (theme) => theme.palette.background.secondary,
                borderColor: (theme) => theme.palette.background.border,
              }}
              onClick={handleClearInput}
              disabled={isUploading || isTailuploading}
            >
              Clear Input
            </Button>
            <LoadingButton
              variant="contained"
              sx={styles.createBtn}
              endIcon={<NextImage src={'/assets/icons/Ai.svg'} alt="" width={24} height={24} />}
              onClick={handleClipGenerate}
              disabled={isFetchingRequiredCredits || clipGenerationLoading || isCheckingEligibility}
              loading={isUploading || isTailuploading}
            >
              Create
            </LoadingButton>
          </Box>
        )}
      </Box>
      <Box sx={styles.clipsSection}>
        <BrollClipsSection
          data={data}
          isLoading={isLoading}
          refetch={refetch}
          allVideos
          params={params}
          setParams={setParams}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
        />
      </Box>

      {openRenderGateModal && (
        <RenderGateModal
          open={openRenderGateModal}
          mode={renderGateModalMode}
          onClose={() => {
            setOpenRenderGateModal(false);
            setRenderGateModalMode('processing');
          }}
          userPlanData={userPlanData}
          agentPlanData={agentPlanData}
          push={push}
        />
      )}
      <CreditDialog
        isOpen={isCreditDialogOpen}
        onClose={closeCreditDialog}
        isFetchingCreditDetail={isFetchingRequiredCredits}
        onConfirm={handleConfirmGeneration}
        creditDetail={{
          availableCredit: requiredCreditsData?.availableCredits,
          creditNeeded: requiredCreditsData?.requiredCredits,
          creditsInUse: requiredCreditsData?.creditsInUse,
        }}
        isLoading={clipGenerationLoading}
        onUpgrade={handleUpgrade}
        enoughCreditConfirmBtnTitle={'Generate'}
        enoughCreditModalTitle="Confirm Generation"
        upgradeButtonTitle="Upgrade"
      />

      {isProccessingModalOpen && (
        <BrollClipProccessingModal
          handleCloseDialog={closeProccessingModal}
          shouldOpenDialog={isProccessingModalOpen}
          data={newGenerationData}
        />
      )}

      {isOpenBrowseBRollModal && (
        <BrowseBRollModal
          handleImageSelect={handleMediaSelect}
          handleCloseDialog={CloseBrowseBRollModal}
          shouldOpenDialog={isOpenBrowseBRollModal}
          isImage={isImage}
          isBrollPage={true}
          showAllmodels={
            clipGenerationData.model === AiVideoModelEnum.RUNWAY_ACT_TWO ||
            clipGenerationData.model === AiVideoModelEnum.KLING_V3_MOTION_CONTROL
          }
        />
      )}

      {isSelectCharacterDialog && (
        <SelectAvatarModal
          shouldOpenDialog={isSelectCharacterDialog}
          handleCloseDialog={closeSelectCharacterDialog}
          handleOnClick={handleCharacterSelect}
        />
      )}

      {showEditor && (
        <ImageEditor
          open={showEditor}
          onClose={() => {
            setShowEditor(false);
            setErrorMsg('');
          }}
          imageFile={imageFile}
          handleFileSelect={handleMediaSelect}
          orientation={clipGenerationData.orientation}
          resolution={clipGenerationData.resolution}
          fromBrollUpload={true}
          errorMsg={errorMsg}
        />
      )}
    </Box>
  );
};

const styles: Styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  title: {
    color: (theme) => theme.palette.text.primary,
    paddingBottom: '10px',
    paddingTop: '10px',
    textAlign: 'center',
  },
  dialogContent: {
    padding: '16px',
    background: (theme) => theme.palette.background.secondary,
  },
  creationForm: {
    padding: '32px',
    borderRadius: '16px',
    backgroundColor: (theme) => theme.palette.background.primary,
    width: '95%',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    borderWidth: '3px',
    borderColor: (theme) => theme.palette.background.border,
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.ternary,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
  },
  clipsSection: {
    width: '95%',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  subHeadingBox: {
    display: 'flex',
    gap: 2,
    p: 2,
    backgroundColor: (theme) => theme.palette.background.activeCard,
    borderRadius: '6px',
    alignItems: 'center',
  },
  exampleBox: {
    p: 2,
    backgroundColor: (theme) => theme.palette.background.activeCard,
    borderRadius: '6px',
  },
  btns: {
    width: '100%',
    display: 'flex',
    height: '56px',
    fontSize: 16,
    gap: 2,
    '& > button': {
      flex: 1,
    },
  },
  text: {
    color: (theme) => theme.palette.text.primary,
  },
  dropdownProps: {
    bgcolor: (theme) => theme.palette.background.secondary,
    maxHeight: '175px',
    maxWidth: 200,
    '& .MuiMenuItem-root': {
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        backgroundColor: (theme) => theme.palette.background.activeCard,
      },
    },
  },
  createBtn: {
    boxShadow: 'none',
    color: (theme) => theme.palette.text.primary,
  },
  uploader: {
    mr: 3,
    height: 70,
    width: 70,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    textAlign: 'center',
    borderRadius: '6px',
    flexDirection: 'column',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.primary,
    cursor: 'pointer',
  },
  CharacterSelectBtn: {
    p: '10px',
    display: 'flex',
    gap: 1,
    borderWidth: 1,
    borderRadius: 56,
    borderColor: (theme) => theme.palette.background.border,
    cursor: 'pointer',
  },
};
